import type { Locale } from "@/lib/i18n";
import { applyPriorityContentEnhancement } from "@/lib/resource-content-enhancements";
import { applyMigrationContentEnhancement } from "@/lib/resource-migration-enhancements";

export type EditorialSource = {
  label: string;
  href: string;
  publisher: string;
};

export type EditorialPath = {
  label: string;
  href: string;
};

export type ResourceEditorialStrategy = {
  key: string;
  displayTitle?: { en: string; es: string };
  /** SERP-length <title> (before the brand suffix) when displayTitle is too long to render untruncated. Falls back to displayTitle. */
  metaTitle?: { en: string; es: string };
  /** Short cover title used inside resource card visuals. Falls back to displayTitle. */
  cardTitle?: { en: string; es: string };
  dek?: { en: string; es: string };
  ownership: { en: string; es: string };
  audience: { en: string; es: string };
  focus: { en: string; es: string };
  excludes?: { en: string; es: string };
  status?: { en: string; es: string };
  sources: EditorialSource[];
  paths?: { en: EditorialPath[]; es: EditorialPath[] };
  linkable?: boolean;
};

type ResourceLike = {
  slug?: string | null;
  slug_es?: string | null;
  title_en?: string | null;
  title_es?: string | null;
  tags_en?: string[] | null;
  tags_es?: string[] | null;
  cluster?: string | null;
};

const SHOPIFY_MIGRATION: EditorialSource = {
  label: "Migrate to Shopify",
  href: "https://help.shopify.com/en/manual/migrating-to-shopify",
  publisher: "Shopify Help Center",
};
const SHOPIFY_REDIRECTS: EditorialSource = {
  label: "Creating and managing URL redirects",
  href: "https://help.shopify.com/en/manual/online-store/menus-and-links/url-redirect",
  publisher: "Shopify Help Center",
};
const SHOPIFY_CHECKOUT: EditorialSource = {
  label: "Technologies for customizing Shopify checkout",
  href: "https://shopify.dev/docs/apps/build/checkout/technologies",
  publisher: "Shopify Developer Documentation",
};
const SHOPIFY_FUNCTIONS: EditorialSource = {
  label: "Shopify Functions APIs",
  href: "https://shopify.dev/docs/api/functions",
  publisher: "Shopify Developer Documentation",
};
const SHOPIFY_SCRIPTS_TRANSITION: EditorialSource = {
  label: "Transitioning from Shopify Scripts to Functions",
  href: "https://help.shopify.com/en/manual/checkout-settings/script-editor/transitioning-to-functions",
  publisher: "Shopify Help Center",
};
const SHOPIFY_PLUS_PRICING: EditorialSource = {
  label: "Shopify Plus pricing",
  href: "https://www.shopify.com/plus/pricing",
  publisher: "Shopify",
};
const SHOPIFY_PLUS_EXPANSION_STORES: EditorialSource = {
  label: "Shopify Plus expansion stores",
  href: "https://help.shopify.com/en/manual/organization-settings/expansion-stores",
  publisher: "Shopify Help Center",
};
const SHOPIFY_DEV: EditorialSource = {
  label: "Shopify developer documentation",
  href: "https://shopify.dev/docs",
  publisher: "Shopify",
};
const SHOPIFY_THEME_PERFORMANCE: EditorialSource = {
  label: "Theme performance best practices",
  href: "https://shopify.dev/docs/storefronts/themes/best-practices/performance",
  publisher: "Shopify Developer Documentation",
};
const GOOGLE_SITE_MOVE: EditorialSource = {
  label: "Site moves with URL changes",
  href: "https://developers.google.com/search/docs/crawling-indexing/site-move-with-url-changes",
  publisher: "Google Search Central",
};
const GOOGLE_CANONICAL: EditorialSource = {
  label: "Canonicalization guidance",
  href: "https://developers.google.com/search/docs/crawling-indexing/canonicalization",
  publisher: "Google Search Central",
};
const GOOGLE_FACETS: EditorialSource = {
  label: "Managing crawling of faceted navigation URLs",
  href: "https://developers.google.com/search/docs/crawling-indexing/crawling-managing-faceted-navigation",
  publisher: "Google Search Central",
};
const GOOGLE_STRUCTURED_DATA: EditorialSource = {
  label: "Structured data documentation",
  href: "https://developers.google.com/search/docs/appearance/structured-data/intro-structured-data",
  publisher: "Google Search Central",
};
const WEB_VITALS: EditorialSource = {
  label: "Core Web Vitals",
  href: "https://web.dev/articles/vitals",
  publisher: "web.dev",
};
const WEB_VITALS_TOOLS: EditorialSource = {
  label: "Core Web Vitals workflows with Google tools",
  href: "https://web.dev/articles/vitals-tools",
  publisher: "web.dev",
};
const FTC_ENDORSEMENTS: EditorialSource = {
  label: "Endorsements, influencers and reviews",
  href: "https://www.ftc.gov/business-guidance/advertising-marketing/endorsements-influencers-reviews",
  publisher: "U.S. Federal Trade Commission",
};
const SHOPIFY_INVESTORS: EditorialSource = {
  label: "Shopify investor relations",
  href: "https://www.shopify.com/investors",
  publisher: "Shopify",
};
const SHOPIFY_COLLABS: EditorialSource = {
  label: "Shopify Collabs documentation",
  href: "https://help.shopify.com/en/manual/promoting-marketing/collabs",
  publisher: "Shopify Help Center",
};
const SHOPIFY_PRODUCTS: EditorialSource = {
  label: "Shopify product model",
  href: "https://shopify.dev/docs/apps/build/graphql/migrate/new-product-model",
  publisher: "Shopify Developer Documentation",
};

const RULES: Array<{ match: RegExp; strategy: ResourceEditorialStrategy }> = [
  {
    match: /shopify-replatforming-playbook|playbook-replatforming-shopify|shopify-plus-migration-masterclass|masterclass.*migration|masterclass.*migraci/i,
    strategy: {
      key: "shopify-replatforming-playbook",
      displayTitle: {
        en: "The Shopify Replatforming Playbook: Process, Cutover, and 90-Day Stabilization",
        es: "Playbook de Replatforming a Shopify: proceso, cutover y estabilización de 90 días",
      },
      cardTitle: {
        en: "Shopify Replatforming Playbook",
        es: "Playbook de Replatforming a Shopify",
      },
      dek: {
        en: "An execution playbook for moving from WooCommerce, Magento, BigCommerce, or a custom platform to Shopify or Shopify Plus: workstreams, data, SEO continuity, launch control, rollback, and stabilization.",
        es: "Un playbook de ejecución para migrar desde WooCommerce, Magento, BigCommerce o una plataforma custom hacia Shopify o Shopify Plus: workstreams, datos, continuidad SEO, control de lanzamiento, rollback y estabilización.",
      },
      ownership: {
        en: "Owns cross-platform Shopify replatforming execution, program governance, cutover, and post-launch stabilization.",
        es: "Esta página cubre la ejecución de un replatform a Shopify, gobierno del programa, cutover y estabilización post-lanzamiento.",
      },
      audience: {
        en: "Heads of ecommerce, technical leads, PMs, and operators coordinating a multi-workstream migration to Shopify or Shopify Plus.",
        es: "Heads of ecommerce, líderes técnicos, PMs y operadores que coordinan una migración a Shopify o Shopify Plus con múltiples workstreams.",
      },
      focus: {
        en: "How to run a Shopify replatform as a controlled business program, from data and SEO workstreams to acceptance gates, cutover command, rollback, and stabilization.",
        es: "Cómo operar un replatform a Shopify como programa de negocio controlado: datos, SEO, acceptance gates, comando de cutover, rollback y estabilización.",
      },
      excludes: {
        en: "It does not own the primary Shopify Plus migration query or decide whether Plus is justified; use the Shopify Plus Migration Guide for that decision.",
        es: "No es la página principal para migración a Shopify Plus ni decide si Plus se justifica; usa la Guía de Migración a Shopify Plus para esa decisión.",
      },
      sources: [SHOPIFY_MIGRATION, SHOPIFY_CHECKOUT, GOOGLE_SITE_MOVE, SHOPIFY_REDIRECTS],
      paths: {
        en: [
          { label: "Plan a Shopify Plus migration", href: "/resources/shopify-plus-migration" },
          { label: "Plan a WooCommerce migration", href: "/resources/woocommerce-to-shopify-migration" },
          { label: "Build the migration timeline", href: "/resources/shopify-migration-timeline" },
          { label: "Plan the redirect map", href: "/resources/shopify-301-redirects-large-catalogs" },
        ],
        es: [
          { label: "Planear una migración a Shopify Plus", href: "/es/recursos/shopify-plus-migration" },
          { label: "Planear una migración desde WooCommerce", href: "/es/recursos/migracion-woocommerce-shopify" },
          { label: "Construir el cronograma", href: "/es/recursos/shopify-migration-timeline" },
          { label: "Planear el mapa de redirects", href: "/es/recursos/shopify-301-redirects-large-catalogs" },
        ],
      },
    },
  },
  {
    match: /shopify-plus-vs-shopify|shopify plus vs shopify|shopify vs shopify plus/i,
    strategy: {
      key: "shopify-plus-plan-comparison",
      ownership: {
        en: "Owns the Shopify plan-comparison and Plus qualification intent.",
        es: "Esta página cubre la comparación de planes y la calificación para Shopify Plus.",
      },
      audience: {
        en: "Operators deciding whether Shopify Plus capabilities justify the platform cost.",
        es: "Operadores que deciden si las capacidades de Shopify Plus justifican el costo.",
      },
      focus: {
        en: "Plan economics, B2B, checkout, Functions, Markets, governance, and the conditions that justify Plus.",
        es: "Economía del plan, B2B, checkout, Functions, Markets, gobierno y condiciones que justifican Plus.",
      },
      excludes: {
        en: "Migration costs, timelines, implementation, and launch risk belong to the Shopify Plus Migration Guide.",
        es: "Costos, tiempos, implementación y riesgo de lanzamiento pertenecen a la Guía de Migración a Shopify Plus.",
      },
      sources: [SHOPIFY_CHECKOUT, SHOPIFY_FUNCTIONS],
      paths: {
        en: [
          { label: "Plan the Shopify Plus migration", href: "/resources/shopify-plus-migration" },
        ],
        es: [
          { label: "Planear la migración a Shopify Plus", href: "/es/recursos/shopify-plus-migration" },
        ],
      },
    },
  },
  {
    match: /shopify-plus-migration(?!-masterclass)|plus migration: when|migraci[oó]n.*plus.*cu[aá]ndo/i,
    strategy: {
      key: "shopify-plus-migration-primary",
      displayTitle: {
        en: "Shopify Plus Migration: Cost, Timeline & Process",
        es: "Migración a Shopify Plus: costo, tiempos y proceso",
      },
      cardTitle: {
        en: "Shopify Plus Migration Guide",
        es: "Guía de Migración a Shopify Plus",
      },
      dek: {
        en: "Plan a Shopify Plus migration with realistic costs, timelines, SEO safeguards, checkout, integrations, testing, cutover and stabilization.",
        es: "Planifica una migración a Shopify Plus con costos, tiempos, SEO, checkout, integraciones, testing, cutover y estabilización.",
      },
      ownership: {
        en: "Owns the primary Shopify Plus migration intent: costs, timeline, process, risk, and when Plus is justified.",
        es: "Esta página cubre la intención principal de migración a Shopify Plus: costos, tiempos, proceso, riesgos y cuándo se justifica.",
      },
      audience: {
        en: "Established ecommerce operators planning an upgrade from Shopify or a replatform from WooCommerce, Magento, BigCommerce, or a custom stack.",
        es: "Operadores ecommerce establecidos que planean subir desde Shopify o migrar desde WooCommerce, Magento, BigCommerce o un stack custom.",
      },
      focus: {
        en: "Migration economics, realistic timelines, architecture, SEO continuity, checkout, B2B, integrations, testing, launch, and stabilization.",
        es: "Economía, tiempos realistas, arquitectura, continuidad SEO, checkout, B2B, integraciones, testing, lanzamiento y estabilización.",
      },
      excludes: {
        en: "Detailed program governance and cutover command belong to the Shopify Replatforming Playbook.",
        es: "El gobierno detallado del programa y el comando de cutover pertenecen al Playbook de Replatforming a Shopify.",
      },
      sources: [
        SHOPIFY_MIGRATION,
        SHOPIFY_CHECKOUT,
        SHOPIFY_FUNCTIONS,
        SHOPIFY_SCRIPTS_TRANSITION,
        SHOPIFY_PLUS_PRICING,
        SHOPIFY_PLUS_EXPANSION_STORES,
        GOOGLE_SITE_MOVE,
      ],
      paths: {
        en: [
          { label: "Run the replatform program", href: "/resources/shopify-replatforming-playbook" },
          { label: "Build the migration timeline", href: "/resources/shopify-migration-timeline" },
          { label: "Plan the redirect map", href: "/resources/shopify-301-redirects-large-catalogs" },
        ],
        es: [
          { label: "Ejecutar el programa de replatform", href: "/es/recursos/playbook-replatforming-shopify" },
          { label: "Construir el cronograma", href: "/es/recursos/shopify-migration-timeline" },
          { label: "Planear el mapa de redirects", href: "/es/recursos/shopify-301-redirects-large-catalogs" },
        ],
      },
    },
  },
  {
    match: /shopify-migration-guide-2026|migrating to shopify in 2026|migrar a shopify en 2026/i,
    strategy: {
      key: "migration-2026-change",
      displayTitle: {
        en: "Migrating to Shopify in 2026: What Changed and What Your Plan Must Account For",
        es: "Migrar a Shopify en 2026: qué cambió y qué debe contemplar tu plan",
      },
      cardTitle: {
        en: "Migrating to Shopify in 2026",
        es: "Migrar a Shopify en 2026",
      },
      dek: {
        en: "A practical Shopify migration guide for inventory, data contracts, storefront scope, SEO continuity, integrations, QA, cutover and launch validation.",
        es: "Una guía práctica de migración a Shopify para inventario, contratos de datos, storefront, continuidad SEO, integraciones, QA, cutover y validación de launch.",
      },
      ownership: {
        en: "Owns the generic Shopify migration guide and how-to intent, including the end-to-end checklist and current 2026 platform considerations.",
        es: "Esta página cubre la intención general de guía y cómo migrar a Shopify, incluido el checklist end-to-end y las consideraciones vigentes de 2026.",
      },
      audience: {
        en: "Ecommerce leaders and implementation owners who need a practical, platform-neutral migration checklist before choosing vendors or a launch date.",
        es: "Líderes ecommerce y responsables de implementación que necesitan un checklist práctico y neutral antes de elegir proveedor o fecha de lanzamiento.",
      },
      focus: {
        en: "Inventory, data contracts, storefront scope, SEO continuity, integrations, acceptance testing, cutover, plus Functions, checkout and product-model changes relevant in 2026.",
        es: "Inventario, contratos de datos, storefront, continuidad SEO, integraciones, pruebas de aceptación y cutover, además de cambios de Functions, checkout y producto relevantes en 2026.",
      },
      excludes: {
        en: "It does not own migration-agency intent, Plus-specific cost and qualification, duration-only queries, or detailed program governance; those have dedicated owners.",
        es: "No cubre intención de agencia, costo o calificación específica de Plus, consultas solo de duración ni gobierno detallado; cada intención tiene un dueño dedicado.",
      },
      status: {
        en: "Time-sensitive analysis. Platform capabilities and deadlines should be verified against the linked first-party documentation.",
        es: "Análisis sensible al tiempo. Verifica capacidades y fechas contra la documentación oficial enlazada.",
      },
      sources: [SHOPIFY_MIGRATION, SHOPIFY_CHECKOUT, SHOPIFY_FUNCTIONS, GOOGLE_SITE_MOVE],
      paths: {
        en: [
          { label: "Estimate the migration timeline", href: "/resources/shopify-migration-timeline" },
          { label: "Run the replatform program", href: "/resources/shopify-replatforming-playbook" },
          { label: "Plan a Shopify Plus migration", href: "/resources/shopify-plus-migration" },
        ],
        es: [
          { label: "Estimar el cronograma", href: "/es/recursos/shopify-migration-timeline" },
          { label: "Ejecutar el programa de replatform", href: "/es/recursos/playbook-replatforming-shopify" },
          { label: "Planear una migración a Shopify Plus", href: "/es/recursos/shopify-plus-migration" },
        ],
      },
    },
  },
  {
    match: /shopify-migration-timeline|migration timeline|cronograma de migraci/i,
    strategy: {
      key: "migration-timeline",
      metaTitle: {
        en: "How Long Does a Shopify Migration Take? Timeline by Phase",
        es: "Cronograma de migración a Shopify para marcas DTC",
      },
      ownership: {
        en: "Owns Shopify migration duration, sequencing, dependencies, and acceptance-gate intent.",
        es: "Esta página cubre duración, secuencia, dependencias y gates de aceptación de una migración.",
      },
      audience: {
        en: "Operators building a realistic internal plan, budget, team allocation, and launch window.",
        es: "Operadores que necesitan un plan interno realista, presupuesto, responsables y ventana de lanzamiento.",
      },
      focus: {
        en: "What happens in each phase, who owns it, what blocks the next phase, and what evidence closes the gate.",
        es: "Qué sucede en cada fase, quién la asume, qué bloquea la siguiente y qué evidencia cierra el gate.",
      },
      excludes: {
        en: "It does not provide the full how-to checklist, compare source platforms, sell migration services, or estimate Shopify Plus costs.",
        es: "No ofrece el checklist completo, compara plataformas origen, vende servicios ni estima costos de Shopify Plus.",
      },
      sources: [SHOPIFY_MIGRATION, SHOPIFY_REDIRECTS, GOOGLE_SITE_MOVE],
      paths: {
        en: [
          { label: "Use the full migration guide", href: "/resources/shopify-migration-guide-2026" },
          { label: "Run the replatform program", href: "/resources/shopify-replatforming-playbook" },
          { label: "Plan a Shopify Plus migration", href: "/resources/shopify-plus-migration" },
        ],
        es: [
          { label: "Usar la guía completa", href: "/es/recursos/shopify-migration-guide-2026" },
          { label: "Ejecutar el programa de replatform", href: "/es/recursos/playbook-replatforming-shopify" },
          { label: "Planear una migración a Shopify Plus", href: "/es/recursos/shopify-plus-migration" },
        ],
      },
    },
  },
  {
    match: /woocommerce.*shopify/i,
    strategy: {
      key: "woocommerce-migration",
      ownership: {
        en: "Owns WooCommerce-specific migration, plugin replacement, content-taxonomy, and SEO-continuity intent.",
        es: "Esta página cubre la migración específica desde WooCommerce, reemplazo de plugins, taxonomía y continuidad SEO.",
      },
      audience: {
        en: "WooCommerce operators whose store already has organic traffic, extensions, subscriptions, and operational dependencies.",
        es: "Operadores WooCommerce con tráfico orgánico, extensiones, suscripciones y dependencias operativas.",
      },
      focus: {
        en: "Plugin-to-capability mapping, WordPress content, customer data, URL changes, subscriptions, and post-launch verification.",
        es: "Mapeo de plugins, contenido WordPress, datos de clientes, cambios de URL, suscripciones y validación post-lanzamiento.",
      },
      sources: [SHOPIFY_MIGRATION, SHOPIFY_REDIRECTS, GOOGLE_SITE_MOVE],
    },
  },
  {
    match: /magento.*shopify/i,
    strategy: {
      key: "magento-migration",
      ownership: {
        en: "Owns Magento-specific data, configurable-product, extension, multi-store, and integration risk.",
        es: "Esta página cubre riesgos específicos de Magento: datos, productos configurables, extensiones, multi-store e integraciones.",
      },
      audience: {
        en: "Magento operators with complex catalogs, custom modules, ERP dependencies, or multiple stores.",
        es: "Operadores Magento con catálogos complejos, módulos custom, ERP o múltiples tiendas.",
      },
      focus: {
        en: "Entity mapping, configurable products, customer groups, extension replacement, integrations, and operational cutover.",
        es: "Mapeo de entidades, productos configurables, customer groups, reemplazo de extensiones, integraciones y cutover.",
      },
      sources: [SHOPIFY_MIGRATION, SHOPIFY_PRODUCTS, SHOPIFY_REDIRECTS, GOOGLE_SITE_MOVE],
    },
  },
  {
    match: /bigcommerce.*shopify/i,
    strategy: {
      key: "bigcommerce-migration",
      ownership: {
        en: "Owns BigCommerce-specific catalog, category, customer-group, Stencil, and integration migration intent.",
        es: "Esta página cubre catálogo, categorías, customer groups, Stencil e integraciones específicas de BigCommerce.",
      },
      audience: {
        en: "BigCommerce teams assessing execution complexity rather than generic platform pros and cons.",
        es: "Equipos BigCommerce que evalúan complejidad de ejecución, no una comparación genérica de plataformas.",
      },
      focus: {
        en: "Category-to-collection mapping, variants, B2B/customer groups, Stencil replacement, checkout, and integration parity.",
        es: "Mapeo de categorías a collections, variantes, B2B/customer groups, reemplazo de Stencil, checkout y paridad de integraciones.",
      },
      sources: [SHOPIFY_MIGRATION, SHOPIFY_PRODUCTS, SHOPIFY_REDIRECTS, GOOGLE_SITE_MOVE],
    },
  },
  {
    match: /llantas cavazos/i,
    strategy: {
      key: "llantas-cavazos-case-study",
      ownership: {
        en: "Owns the documented Llantas Cavazos Shopify migration case, its delivery evidence, and its limitations.",
        es: "Esta página cubre el caso documentado de migración de Llantas Cavazos a Shopify, su evidencia y sus límites.",
      },
      audience: {
        en: "Ecommerce leaders evaluating how a catalog-heavy migration was scoped, controlled, and verified in a real engagement.",
        es: "Líderes ecommerce que evalúan cómo se definió, controló y verificó una migración real con catálogo amplio.",
      },
      focus: {
        en: "Initial constraints, migration decisions, validation evidence, what can be attributed to the work, and what was not measured.",
        es: "Restricciones iniciales, decisiones, evidencia de validación, qué puede atribuirse al trabajo y qué no fue medido.",
      },
      sources: [SHOPIFY_MIGRATION, SHOPIFY_REDIRECTS, GOOGLE_SITE_MOVE],
      paths: {
        en: [
          { label: "Plan a Shopify Plus migration", href: "/resources/shopify-plus-migration" },
          { label: "Use the Shopify migration guide", href: "/resources/shopify-migration-guide-2026" },
          { label: "Evaluate Shopify migration services", href: "/shopify-migration" },
        ],
        es: [
          { label: "Planear una migración a Shopify Plus", href: "/es/recursos/shopify-plus-migration" },
          { label: "Usar la guía de migración a Shopify", href: "/es/recursos/shopify-migration-guide-2026" },
          { label: "Evaluar servicios de migración a Shopify", href: "/es/migracion-shopify" },
        ],
      },
    },
  },
  {
    match: /301.*redirect|redirects.*large|redirect.*catalog/i,
    strategy: {
      key: "redirect-map",
      ownership: {
        en: "Owns large-catalog redirect inventory, matching logic, exceptions, QA, and monitoring intent.",
        es: "Esta página cubre inventario, lógica de matching, excepciones, QA y monitoreo de redirects a escala.",
      },
      audience: {
        en: "SEO leads, migration PMs, and developers responsible for preserving discoverability during URL change.",
        es: "Líderes SEO, PMs de migración y developers responsables de preservar visibilidad durante cambios de URL.",
      },
      focus: {
        en: "How to construct and validate the URL map, not the entire migration program.",
        es: "Cómo construir y validar el mapa de URLs, no el programa completo de migración.",
      },
      sources: [SHOPIFY_REDIRECTS, GOOGLE_SITE_MOVE, GOOGLE_CANONICAL],
    },
  },
  {
    match: /rise.*migrating|rise.*migraci|why brands are migrating|por qu[eé].*migran/i,
    strategy: {
      key: "replatform-business-case",
      displayTitle: {
        en: "The Business Case for Replatforming to Shopify in 2026",
        es: "El business case para migrar a Shopify en 2026",
      },
      cardTitle: {
        en: "Business Case for Replatforming",
        es: "Business case para migrar",
      },
      ownership: {
        en: "Owns executive replatform justification and platform-economics intent.",
        es: "Esta página cubre la justificación ejecutiva y la economía de un replatform.",
      },
      audience: {
        en: "Founders, ecommerce leaders, and finance or operations stakeholders deciding whether a replatform deserves priority.",
        es: "Founders y líderes de ecommerce, finanzas u operaciones que deciden si un replatform merece prioridad.",
      },
      focus: {
        en: "The conditions that make a platform change economically rational: ownership cost, release velocity, operational drag, and opportunity cost.",
        es: "Las condiciones que vuelven racional el cambio: costo de ownership, velocidad de release, fricción operativa y costo de oportunidad.",
      },
      excludes: {
        en: "It is not a step-by-step migration guide or a platform-specific implementation checklist.",
        es: "No es una guía paso a paso ni un checklist de implementación por plataforma.",
      },
      status: {
        en: "Market analysis dated 2026. Recheck platform announcements and financial data before relying on any time-sensitive figure.",
        es: "Análisis de mercado fechado en 2026. Verifica anuncios y cifras antes de usar datos sensibles al tiempo.",
      },
      sources: [SHOPIFY_INVESTORS, SHOPIFY_MIGRATION, SHOPIFY_DEV],
    },
  },
  {
    match: /performance optimization guide|performance.*pillar|gu[ií]a.*optimizaci[oó]n.*performance/i,
    strategy: {
      key: "performance-pillar",
      ownership: {
        en: "Owns the broad Shopify performance strategy, diagnosis, prioritization, and release-validation intent.",
        es: "Esta página cubre estrategia, diagnóstico, priorización y validación de performance en Shopify.",
      },
      audience: {
        en: "Ecommerce operators who need to connect storefront speed to business symptoms and an actionable engineering plan.",
        es: "Operadores ecommerce que necesitan conectar velocidad, síntomas de negocio y un plan de ingeniería.",
      },
      focus: {
        en: "The complete diagnostic framework and prioritization model.",
        es: "El framework completo de diagnóstico y priorización.",
      },
      excludes: {
        en: "Metric-specific remediation belongs to the Core Web Vitals guide; execution checks belong to the checklist.",
        es: "La remediación por métrica pertenece a Core Web Vitals; la ejecución paso a paso, al checklist.",
      },
      sources: [WEB_VITALS, WEB_VITALS_TOOLS, SHOPIFY_THEME_PERFORMANCE],
    },
  },
  {
    match: /performance.*checklist|optimization checklist/i,
    strategy: {
      key: "performance-checklist",
      cardTitle: {
        en: "Performance Checklist for $500k+ Brands",
        es: "Checklist de Performance para marcas $500k+",
      },
      ownership: {
        en: "Owns the operational Shopify performance audit and pass/fail checklist intent.",
        es: "Esta página cubre la auditoría operativa y el checklist pass/fail de performance.",
      },
      audience: {
        en: "Operators and technical leads running a structured audit before committing engineering time.",
        es: "Operadores y líderes técnicos que ejecutan una auditoría estructurada antes de asignar ingeniería.",
      },
      focus: {
        en: "Check, tool, evidence, owner, priority, and acceptance threshold.",
        es: "Check, herramienta, evidencia, owner, prioridad y umbral de aceptación.",
      },
      excludes: {
        en: "It is not the broad performance strategy or a deep explanation of each Core Web Vital.",
        es: "No es la estrategia general ni una explicación profunda de cada Core Web Vital.",
      },
      sources: [WEB_VITALS, WEB_VITALS_TOOLS, SHOPIFY_THEME_PERFORMANCE],
    },
  },
  {
    match: /core web vitals|lcp.*inp.*cls|speed optimization/i,
    strategy: {
      key: "core-web-vitals",
      ownership: {
        en: "Owns Shopify-specific LCP, INP, and CLS diagnosis and remediation intent.",
        es: "Esta página cubre diagnóstico y remediación de LCP, INP y CLS en Shopify.",
      },
      audience: {
        en: "Technical leads and ecommerce operators who already know the store is slow and need to identify the responsible system.",
        es: "Líderes técnicos y operadores que ya saben que la tienda está lenta y necesitan localizar el sistema responsable.",
      },
      focus: {
        en: "Field data versus lab data, Shopify root causes by metric, and controlled validation after release.",
        es: "Field data vs lab data, causas Shopify por métrica y validación controlada después del release.",
      },
      sources: [WEB_VITALS, WEB_VITALS_TOOLS, SHOPIFY_THEME_PERFORMANCE],
    },
  },
  {
    match: /app bloat|too many apps|exceso de apps/i,
    strategy: {
      key: "app-bloat",
      ownership: {
        en: "Owns Shopify app-stack performance, governance, and removal-decision intent.",
        es: "Esta página cubre performance y gobierno del app stack, incluyendo decisiones de remoción.",
      },
      audience: {
        en: "Operators with a mature app stack, unexplained storefront regressions, or unclear app ownership.",
        es: "Operadores con un app stack maduro, regresiones inexplicables o ownership poco claro.",
      },
      focus: {
        en: "Inventory, request cost, theme residue, business dependency, replacement options, and validation before removal.",
        es: "Inventario, costo de requests, residuos en theme, dependencia de negocio, reemplazo y validación antes de remover.",
      },
      sources: [SHOPIFY_THEME_PERFORMANCE, WEB_VITALS, WEB_VITALS_TOOLS],
    },
  },
  {
    match: /cro playbook/i,
    strategy: {
      key: "cro-pillar",
      ownership: {
        en: "Owns the Shopify CRO operating system: measurement, diagnosis, prioritization, experimentation, and learning cadence.",
        es: "Esta página cubre el sistema operativo de CRO: medición, diagnóstico, prioridad, experimentación y aprendizaje.",
      },
      audience: {
        en: "Ecommerce leaders who need a repeatable conversion program rather than isolated design ideas.",
        es: "Líderes ecommerce que necesitan un programa repetible, no ideas aisladas de diseño.",
      },
      focus: {
        en: "How to run CRO as a decision process and operating cadence.",
        es: "Cómo operar CRO como proceso de decisión y cadencia continua.",
      },
      excludes: {
        en: "The 12 Fixes guide owns the tactical action list; specialized PDP and checkout articles own implementation detail.",
        es: "La guía de 12 fixes cubre tácticas; PDP y checkout cubren implementación especializada.",
      },
      sources: [SHOPIFY_CHECKOUT, WEB_VITALS],
      paths: {
        en: [
          { label: "Prioritize tactical CRO fixes", href: "/resources/shopify-cro-conversion-rate" },
          { label: "Evaluate Shopify CRO services", href: "/shopify-cro" },
          { label: "Browse the conversion hub", href: "/resources/conversion" },
        ],
        es: [
          { label: "Priorizar mejoras tácticas de CRO", href: "/es/recursos/shopify-cro-conversion" },
          { label: "Evaluar servicios CRO para Shopify", href: "/es/cro-shopify" },
          { label: "Explorar el hub de conversión", href: "/es/recursos/conversion" },
        ],
      },
    },
  },
  {
    match: /12 fixes|conversion rate.*dtc/i,
    strategy: {
      key: "cro-tactical",
      ownership: {
        en: "Owns tactical Shopify CRO opportunities ranked by observable evidence and implementation risk.",
        es: "Esta página cubre oportunidades tácticas de CRO ordenadas por evidencia y riesgo de implementación.",
      },
      audience: {
        en: "Established DTC operators looking for the next high-confidence diagnostic or experiment, not generic best practices.",
        es: "Operadores DTC establecidos que buscan el siguiente diagnóstico o experimento con buena evidencia.",
      },
      focus: {
        en: "For each fix: symptom, evidence, applicability, trade-off, implementation path, and measurement plan.",
        es: "Para cada fix: síntoma, evidencia, aplicabilidad, trade-off, implementación y medición.",
      },
      excludes: {
        en: "It is not the complete CRO operating model; use the CRO Playbook for that.",
        es: "No es el modelo operativo completo; para eso está el CRO Playbook.",
      },
      sources: [SHOPIFY_CHECKOUT, WEB_VITALS],
      paths: {
        en: [
          { label: "Use the Shopify CRO operating model", href: "/resources/shopify-conversion-rate-optimization-playbook" },
          { label: "Evaluate Shopify CRO services", href: "/shopify-cro" },
          { label: "Browse the conversion hub", href: "/resources/conversion" },
        ],
        es: [
          { label: "Usar el modelo operativo de CRO", href: "/es/recursos/playbook-cro-shopify" },
          { label: "Evaluar servicios CRO para Shopify", href: "/es/cro-shopify" },
          { label: "Explorar el hub de conversión", href: "/es/recursos/conversion" },
        ],
      },
    },
  },
  {
    match: /size guide|gu[ií]a de tallas/i,
    strategy: {
      key: "size-guide",
      ownership: {
        en: "Owns Shopify size-guide architecture, PDP usability, and returns-reduction intent.",
        es: "Esta página cubre arquitectura de guía de tallas, usabilidad PDP y reducción de devoluciones.",
      },
      audience: {
        en: "Apparel and size-sensitive brands deciding between a native section, metaobjects, or an app.",
        es: "Marcas de moda o productos sensibles a talla que eligen entre section nativo, metaobjects o app.",
      },
      focus: {
        en: "Customer uncertainty, catalog complexity, accessibility, international sizing, maintenance, and measurement.",
        es: "Incertidumbre del cliente, complejidad de catálogo, accesibilidad, tallas internacionales, mantenimiento y medición.",
      },
      sources: [SHOPIFY_DEV, GOOGLE_STRUCTURED_DATA],
    },
  },
  {
    match: /common shopify issues|problemas comunes/i,
    strategy: {
      key: "diagnostic-index",
      displayTitle: {
        en: "Shopify Diagnostic Index: Common Symptoms, Evidence, and the Right Specialist Path",
        es: "Índice de diagnóstico Shopify: síntomas, evidencia y la ruta especialista correcta",
      },
      cardTitle: {
        en: "Shopify Diagnostic Index",
        es: "Índice de diagnóstico Shopify",
      },
      dek: {
        en: "A fast triage directory for established stores. Start with the symptom, identify the evidence, and continue to the specialist guide instead of treating every problem as the same audit.",
        es: "Un directorio de triage para tiendas establecidas. Empieza por el síntoma, identifica la evidencia y continúa a la guía especialista correcta.",
      },
      ownership: {
        en: "Owns cross-discipline triage and routing intent, not any specialist topic in full.",
        es: "Esta página cubre triage y routing entre disciplinas, no desarrolla cada tema especialista.",
      },
      audience: {
        en: "Operators who know something is wrong but do not yet know whether the primary constraint is performance, CRO, SEO, migration, or measurement.",
        es: "Operadores que detectan un problema pero aún no saben si la restricción principal es performance, CRO, SEO, migración o medición.",
      },
      focus: {
        en: "Symptom, evidence to collect, likely owner, urgency, and the next specialist resource.",
        es: "Síntoma, evidencia necesaria, owner probable, urgencia y siguiente recurso especialista.",
      },
      excludes: {
        en: "It should not compete with the performance, CRO, technical SEO, or migration pillars.",
        es: "No debe competir con los pilares de performance, CRO, SEO técnico o migración.",
      },
      sources: [WEB_VITALS, SHOPIFY_CHECKOUT, GOOGLE_CANONICAL, SHOPIFY_REDIRECTS],
    },
  },
  {
    match: /technical seo playbook|seo t[eé]cnico.*playbook/i,
    strategy: {
      key: "technical-seo-pillar",
      ownership: {
        en: "Owns the broad Shopify technical SEO audit sequence and prioritization intent.",
        es: "Esta página cubre la secuencia general de auditoría y priorización de SEO técnico.",
      },
      audience: {
        en: "SEO and ecommerce leaders who need to distinguish platform defaults from implementation failures.",
        es: "Líderes SEO y ecommerce que necesitan separar defaults de plataforma de fallos de implementación.",
      },
      focus: {
        en: "Crawling, indexation, canonicals, architecture, schema, rendering, and validation as one system.",
        es: "Crawling, indexación, canonicals, arquitectura, schema, rendering y validación como un sistema.",
      },
      excludes: {
        en: "Collection pages, canonicals, facets, and redirect maps each have a dedicated implementation guide.",
        es: "Collection pages, canonicals, filtros y redirects tienen guías de implementación propias.",
      },
      sources: [GOOGLE_CANONICAL, GOOGLE_FACETS, GOOGLE_STRUCTURED_DATA, GOOGLE_SITE_MOVE],
    },
  },
  {
    match: /collection pages.*ranking|collection.*rank/i,
    strategy: {
      key: "collection-seo",
      cardTitle: {
        en: "Collection Pages That Rank",
        es: "Collections que rankean",
      },
      ownership: {
        en: "Owns Shopify collection-page search intent, architecture, internal-link, and indexability diagnosis.",
        es: "Esta página cubre intención, arquitectura, enlaces internos e indexabilidad de collections.",
      },
      audience: {
        en: "Ecommerce and SEO teams with category pages that receive impressions but fail to earn stable rankings or qualified clicks.",
        es: "Equipos ecommerce y SEO con categorías que reciben impresiones pero no logran rankings o clics calificados.",
      },
      focus: {
        en: "Query-to-collection fit, inventory, internal links, pagination, content, schema, and competing URLs.",
        es: "Fit entre query y collection, inventario, enlaces internos, paginación, contenido, schema y URLs competidoras.",
      },
      sources: [GOOGLE_CANONICAL, GOOGLE_STRUCTURED_DATA, GOOGLE_FACETS],
    },
  },
  {
    match: /canonical tags|canonical.*shopify/i,
    strategy: {
      key: "canonicals",
      cardTitle: {
        en: "Shopify Canonical Tags",
        es: "Canonical tags en Shopify",
      },
      ownership: {
        en: "Owns declared-versus-selected canonical diagnosis on Shopify.",
        es: "Esta página cubre la diferencia entre canonical declarado y canonical seleccionado por Google.",
      },
      audience: {
        en: "SEO and development teams investigating duplicate URLs, multi-collection paths, parameter pages, or conflicting app output.",
        es: "Equipos SEO y desarrollo que investigan duplicados, rutas multi-collection, parámetros o output conflictivo de apps.",
      },
      focus: {
        en: "Shopify defaults, internal-link consistency, rendered canonicals, schema URLs, and Google-selected alternatives.",
        es: "Defaults Shopify, consistencia de enlaces, canonicals renderizados, URLs en schema y alternativas elegidas por Google.",
      },
      sources: [GOOGLE_CANONICAL, GOOGLE_STRUCTURED_DATA],
    },
  },
  {
    match: /faceted navigation|navegaci[oó]n facetada/i,
    strategy: {
      key: "facets",
      cardTitle: {
        en: "Shopify Faceted Navigation",
        es: "Filtros de Shopify",
      },
      ownership: {
        en: "Owns Shopify filter-URL crawling, indexability, canonical, and landing-page decision intent.",
        es: "Esta página cubre decisiones de crawling, indexabilidad, canonical y landing pages para filtros Shopify.",
      },
      audience: {
        en: "SEO and merchandising teams balancing useful filtered experiences against crawl expansion and duplicate inventory.",
        es: "Equipos SEO y merchandising que equilibran filtros útiles contra expansión de crawl y duplicados.",
      },
      focus: {
        en: "A decision matrix, not universal robots.txt or noindex rules.",
        es: "Una matriz de decisión, no reglas universales de robots.txt o noindex.",
      },
      sources: [GOOGLE_FACETS, GOOGLE_CANONICAL],
    },
  },
  {
    match: /checkout extensibility/i,
    strategy: {
      key: "checkout-extensibility",
      displayTitle: {
        en: "Shopify Checkout Extensibility: A Current Architecture and Audit Guide",
        es: "Shopify Checkout Extensibility: guía actual de arquitectura y auditoría",
      },
      cardTitle: {
        en: "Checkout Extensibility Guide",
        es: "Guía de Checkout Extensibility",
      },
      dek: {
        en: "What is currently supported, which technology owns each requirement, and how to audit legacy checkout behavior without relying on expired countdowns.",
        es: "Qué se soporta hoy, qué tecnología resuelve cada requisito y cómo auditar comportamiento legado sin depender de fechas ya vencidas.",
      },
      ownership: {
        en: "Owns checkout customization architecture and current-state audit intent.",
        es: "Esta página cubre arquitectura de personalización de checkout y auditoría del estado actual.",
      },
      audience: {
        en: "Plus merchants, ecommerce leads, and developers mapping business requirements to UI Extensions, Functions, pixels, branding, and apps.",
        es: "Merchants Plus, líderes ecommerce y developers que mapean requisitos a UI Extensions, Functions, pixels, branding y apps.",
      },
      focus: {
        en: "Requirement-to-technology decisions, legacy inventory, implementation boundaries, testing, and ownership.",
        es: "Decisiones requisito-tecnología, inventario legado, límites, pruebas y ownership.",
      },
      status: {
        en: "Current-state guide. Verify API versions and supported extension targets in Shopify's documentation before implementation.",
        es: "Guía de estado actual. Verifica versiones API y extension targets soportados antes de implementar.",
      },
      sources: [SHOPIFY_CHECKOUT, SHOPIFY_FUNCTIONS, SHOPIFY_DEV],
    },
  },
  {
    match: /scripts deprecation|scripts.*deprecaci|scripts.*migration.*2026/i,
    strategy: {
      key: "scripts-recovery",
      displayTitle: {
        en: "After Shopify Scripts: Recovery, Functions Replacement, and Validation Checklist",
        es: "Después de Shopify Scripts: recuperación, reemplazo con Functions y checklist de validación",
      },
      cardTitle: {
        en: "After Shopify Scripts",
        es: "Después de Shopify Scripts",
      },
      dek: {
        en: "The deadline has passed. This guide now helps Plus teams inventory surviving business logic, rebuild it with Functions, and verify discounts, delivery, and payment behavior.",
        es: "La fecha límite ya pasó. Esta guía ayuda a equipos Plus a inventariar lógica, reconstruirla con Functions y validar descuentos, entrega y pagos.",
      },
      ownership: {
        en: "Owns post-deprecation recovery and Scripts-to-Functions replacement intent.",
        es: "Esta página cubre recuperación post-deprecación y reemplazo de Scripts por Functions.",
      },
      audience: {
        en: "Shopify Plus teams that missed part of the migration, inherited undocumented Scripts logic, or need to verify a completed replacement.",
        es: "Equipos Plus que heredaron lógica no documentada, quedaron incompletos o necesitan validar el reemplazo.",
      },
      focus: {
        en: "Inventory, behavioral equivalence, Functions architecture, test cases, observability, and rollback planning.",
        es: "Inventario, equivalencia de comportamiento, arquitectura Functions, casos de prueba, observabilidad y rollback.",
      },
      status: {
        en: "Post-deprecation guidance updated after June 30, 2026.",
        es: "Guía post-deprecación actualizada después del 30 de junio de 2026.",
      },
      sources: [SHOPIFY_FUNCTIONS, SHOPIFY_CHECKOUT, SHOPIFY_DEV],
    },
  },
  {
    match: /functions explained|shopify functions|functions: qu[eé]/i,
    strategy: {
      key: "functions-explainer",
      ownership: {
        en: "Owns merchant-friendly Shopify Functions concepts and requirement-to-Function fit.",
        es: "Esta página explica Shopify Functions en lenguaje de negocio y cuándo aplica cada tipo de requisito.",
      },
      audience: {
        en: "Merchants and technical stakeholders deciding whether checkout logic belongs in a Function, an extension, an app, or another system.",
        es: "Merchants y stakeholders técnicos que deciden si la lógica pertenece a Function, extension, app u otro sistema.",
      },
      focus: {
        en: "Business outcomes first, then runtime, API version, configuration, limits, and deployment ownership.",
        es: "Resultados de negocio primero; después runtime, versión API, configuración, límites y deployment.",
      },
      status: {
        en: "Version-sensitive technical guide. Confirm available Function APIs and language support before implementation.",
        es: "Guía técnica sensible a versión. Confirma APIs y lenguajes soportados antes de implementar.",
      },
      sources: [SHOPIFY_FUNCTIONS, SHOPIFY_CHECKOUT, SHOPIFY_DEV],
    },
  },
  {
    match: /development guide|gu[ií]a de desarrollo shopify/i,
    strategy: {
      key: "development-pillar",
      ownership: {
        en: "Owns the architecture decision for where Shopify functionality should live.",
        es: "Esta página cubre la decisión arquitectónica de dónde debe vivir cada funcionalidad Shopify.",
      },
      audience: {
        en: "Ecommerce leaders and developers choosing between theme code, Functions, apps, admin extensions, integrations, and headless storefronts.",
        es: "Líderes ecommerce y developers que eligen entre theme, Functions, apps, extensiones, integraciones y headless.",
      },
      focus: {
        en: "Persistence, rendering surface, checkout access, admin workflow, background work, deployment, and long-term ownership.",
        es: "Persistencia, superficie de render, acceso a checkout, admin workflow, background work, deployment y ownership.",
      },
      sources: [SHOPIFY_DEV, SHOPIFY_FUNCTIONS, SHOPIFY_CHECKOUT, SHOPIFY_PRODUCTS],
      paths: {
        en: [
          { label: "Decide between custom development and apps", href: "/resources/custom-shopify-solutions-for-established-brands" },
          { label: "Compare AI builders with professional development", href: "/resources/ai-vs-professional-web-developers-2026" },
          { label: "Evaluate a Shopify development agency", href: "/resources/how-to-choose-a-shopify-development-agency" },
          { label: "Build a Liquid recommender", href: "/resources/shopify-liquid-product-recommender" },
          { label: "Weigh headless Shopify against a Liquid storefront", href: "/blog/shopify-headless-commerce" },
        ],
        es: [
          { label: "Decidir entre desarrollo custom y apps", href: "/es/recursos/custom-shopify-solutions-for-established-brands" },
          { label: "Comparar AI builders con desarrollo profesional", href: "/es/recursos/ai-vs-professional-web-developers-2026" },
          { label: "Evaluar una agencia de desarrollo Shopify", href: "/es/recursos/how-to-choose-a-shopify-development-agency" },
          { label: "Construir un recomendador en Liquid", href: "/es/recursos/shopify-liquid-product-recommender" },
        ],
      },
      linkable: true,
    },
  },
  {
    match: /custom shopify solutions|soluciones custom/i,
    strategy: {
      key: "custom-qualification",
      displayTitle: {
        en: "When Custom Shopify Development Is Justified, and When an App Is Better",
        es: "Cuándo se justifica desarrollo custom en Shopify y cuándo conviene una app",
      },
      cardTitle: {
        en: "Custom Development vs Apps",
        es: "Desarrollo custom vs apps",
      },
      ownership: {
        en: "Owns custom-development qualification and build-versus-buy intent.",
        es: "Esta página cubre calificación de desarrollo custom y la decisión build vs buy.",
      },
      audience: {
        en: "Established brands facing a requirement that off-the-shelf apps only partially solve.",
        es: "Marcas establecidas con requisitos que las apps sólo resuelven parcialmente.",
      },
      focus: {
        en: "Five conditions that justify custom work, five counterexamples, total ownership cost, maintenance, data contracts, and operational risk.",
        es: "Cinco condiciones que justifican custom, cinco contraejemplos, costo de ownership, mantenimiento, contratos de datos y riesgo.",
      },
      sources: [SHOPIFY_DEV, SHOPIFY_FUNCTIONS, SHOPIFY_CHECKOUT],
      paths: {
        en: [
          { label: "Study the Shopify development architecture", href: "/resources/shopify-development-guide" },
          { label: "Compare AI builders with professional development", href: "/resources/ai-vs-professional-web-developers-2026" },
          { label: "Evaluate Shopify development services", href: "/shopify-experts" },
        ],
        es: [
          { label: "Estudiar la arquitectura de desarrollo Shopify", href: "/es/recursos/shopify-development-guide" },
          { label: "Comparar AI builders con desarrollo profesional", href: "/es/recursos/ai-vs-professional-web-developers-2026" },
          { label: "Evaluar servicios de desarrollo Shopify", href: "/es/desarrollo-shopify" },
        ],
      },
    },
  },
  {
    match: /store setup playbook|setup.*serious dtc|setup.*dtc en serio/i,
    strategy: {
      key: "established-launch",
      displayTitle: {
        en: "Shopify Launch Checklist for Established DTC Brands",
        es: "Checklist de lanzamiento Shopify para marcas DTC establecidas",
      },
      cardTitle: {
        en: "Launch Checklist for DTC Brands",
        es: "Checklist de lanzamiento DTC",
      },
      ownership: {
        en: "Owns established-brand launch readiness, not beginner store setup.",
        es: "Esta página cubre readiness de lanzamiento para marcas establecidas, no setup para principiantes.",
      },
      audience: {
        en: "Brands with customers, revenue, marketing systems, operational dependencies, and a launch they cannot afford to improvise.",
        es: "Marcas con clientes, revenue, sistemas de marketing, dependencias operativas y un lanzamiento que no pueden improvisar.",
      },
      focus: {
        en: "Catalog, storefront, payments, shipping, tax, analytics, integrations, ownership, and launch acceptance.",
        es: "Catálogo, storefront, pagos, shipping, impuestos, analytics, integraciones, ownership y aceptación de lanzamiento.",
      },
      sources: [SHOPIFY_PRODUCTS, SHOPIFY_DEV, SHOPIFY_CHECKOUT],
    },
  },
  {
    match: /product recommender|recomendador.*liquid/i,
    strategy: {
      key: "liquid-recommender",
      ownership: {
        en: "Owns a lightweight, no-app Shopify recommendation pattern and its limitations.",
        es: "Esta página cubre un patrón ligero de recomendaciones sin app y sus límites.",
      },
      audience: {
        en: "Shopify developers and technical operators who need a fast merchandising pattern without pretending it is a full personalization system.",
        es: "Developers y operadores que necesitan un patrón rápido sin confundirlo con personalización avanzada.",
      },
      focus: {
        en: "Available storefront signals, fallback logic, caching, Liquid constraints, measurement, and when to graduate to an app or service.",
        es: "Señales disponibles, fallback, caching, límites Liquid, medición y cuándo migrar a una app o servicio.",
      },
      sources: [SHOPIFY_DEV, SHOPIFY_PRODUCTS],
      linkable: true,
    },
  },
  {
    match: /choose a shopify expert|elegir un experto/i,
    strategy: {
      key: "expert-selection",
      displayTitle: {
        en: "Shopify Expert Vetting Checklist: Evidence, Code Quality, and Continuity",
        es: "Checklist para evaluar un experto Shopify: evidencia, calidad de código y continuidad",
      },
      cardTitle: {
        en: "Shopify Expert Vetting Checklist",
        es: "Checklist para evaluar un experto Shopify",
      },
      dek: {
        en: "A practical checklist for vetting one Shopify specialist: technical evidence, code review, availability, ownership, continuity, and bus-factor risk.",
        es: "Un checklist práctico para evaluar a un especialista Shopify: evidencia técnica, revisión de código, disponibilidad, ownership, continuidad y riesgo de dependencia.",
      },
      ownership: {
        en: "Owns evaluation of an individual Shopify specialist or narrowly scoped small partner.",
        es: "Esta página cubre cómo evaluar a un especialista individual o partner pequeño con scope limitado.",
      },
      audience: {
        en: "Merchants who need one accountable specialist for a bounded requirement.",
        es: "Merchants que necesitan un especialista responsable para un requisito acotado.",
      },
      focus: {
        en: "Technical evidence, availability, code ownership, review process, continuity, and bus-factor risk.",
        es: "Evidencia técnica, disponibilidad, ownership del código, revisión, continuidad y bus factor.",
      },
      excludes: {
        en: "Multidisciplinary agency procurement belongs to the development-agency buyer's guide.",
        es: "La compra de una agencia multidisciplinaria pertenece a la guía de selección de agencia.",
      },
      sources: [SHOPIFY_DEV],
      paths: {
        en: [
          { label: "Evaluate a multidisciplinary Shopify agency", href: "/resources/how-to-choose-a-shopify-development-agency" },
          { label: "Study the Shopify development architecture", href: "/resources/shopify-development-guide" },
          { label: "Evaluate Shopify development services", href: "/shopify-experts" },
        ],
        es: [
          { label: "Evaluar una agencia Shopify multidisciplinaria", href: "/es/recursos/how-to-choose-a-shopify-development-agency" },
          { label: "Estudiar la arquitectura de desarrollo Shopify", href: "/es/recursos/shopify-development-guide" },
          { label: "Evaluar servicios de desarrollo Shopify", href: "/es/desarrollo-shopify" },
        ],
      },
    },
  },
  {
    match: /choose a shopify development agency|elegir.*agencia.*shopify/i,
    strategy: {
      key: "agency-selection",
      cardTitle: {
        en: "Choosing a Shopify Agency",
        es: "Elegir una agencia Shopify",
      },
      ownership: {
        en: "Owns procurement of a multidisciplinary Shopify agency with governance and continuity.",
        es: "Esta página cubre procurement de una agencia Shopify multidisciplinaria con gobierno y continuidad.",
      },
      audience: {
        en: "Established brands buying cross-functional delivery across engineering, SEO, CRO, analytics, QA, and release ownership.",
        es: "Marcas establecidas que compran delivery entre ingeniería, SEO, CRO, analytics, QA y releases.",
      },
      focus: {
        en: "Discovery, team composition, QA, references, commercial model, contract boundaries, release responsibility, and post-launch accountability.",
        es: "Discovery, equipo, QA, referencias, modelo comercial, límites contractuales, releases y accountability post-lanzamiento.",
      },
      excludes: {
        en: "Hiring one specialist belongs to the Shopify Expert guide.",
        es: "Contratar a un especialista individual pertenece a la guía de Shopify Expert.",
      },
      sources: [SHOPIFY_DEV],
      paths: {
        en: [
          { label: "Vet one Shopify specialist", href: "/resources/how-to-choose-shopify-expert" },
          { label: "Study the Shopify development architecture", href: "/resources/shopify-development-guide" },
          { label: "Evaluate Shopify development services", href: "/shopify-experts" },
        ],
        es: [
          { label: "Evaluar a un especialista Shopify", href: "/es/recursos/como-elegir-un-experto-shopify" },
          { label: "Estudiar la arquitectura de desarrollo Shopify", href: "/es/recursos/shopify-development-guide" },
          { label: "Evaluar servicios de desarrollo Shopify", href: "/es/desarrollo-shopify" },
        ],
      },
    },
  },
  {
    match: /new rules for ai shoppers|reglas.*compradores.*ia/i,
    strategy: {
      key: "ai-rules-news",
      ownership: {
        en: "Owns the dated May 2026 Shopify AI-shopping policy and platform-update analysis.",
        es: "Esta página cubre el análisis fechado de cambios de mayo de 2026 para compras con IA.",
      },
      audience: {
        en: "Ecommerce operators who need to know what changed, what was announced, and what required action at that moment.",
        es: "Operadores ecommerce que necesitan saber qué cambió, qué fue anunciado y qué requería acción en ese momento.",
      },
      focus: {
        en: "Announcement, date, affected surfaces, merchant implications, and a clearly maintained update log.",
        es: "Anuncio, fecha, superficies afectadas, implicaciones y update log mantenido.",
      },
      excludes: {
        en: "The evergreen implementation model belongs to the Agentic Storefronts guide.",
        es: "El modelo evergreen de implementación pertenece a Agentic Storefronts.",
      },
      status: {
        en: "Dated platform analysis from May 2026. Confirm current availability and partner support before acting.",
        es: "Análisis de plataforma fechado en mayo de 2026. Confirma disponibilidad actual antes de actuar.",
      },
      sources: [SHOPIFY_DEV, SHOPIFY_INVESTORS],
      paths: {
        en: [
          { label: "Use the evergreen Agentic Storefronts readiness guide", href: "/resources/shopify-agentic-storefronts-product-data" },
          { label: "Study the Shopify development architecture", href: "/resources/shopify-development-guide" },
        ],
        es: [
          { label: "Usar la guía evergreen de readiness para Agentic Storefronts", href: "/es/recursos/shopify-agentic-storefronts-product-data" },
          { label: "Estudiar la arquitectura de desarrollo Shopify", href: "/es/recursos/shopify-development-guide" },
        ],
      },
    },
  },
  {
    match: /agentic storefronts/i,
    strategy: {
      key: "agentic-evergreen",
      displayTitle: {
        en: "Agentic Storefronts for Shopify: Product Data, Merchant Control, and Operational Readiness",
        es: "Agentic Storefronts en Shopify: datos de producto, control merchant y readiness operativo",
      },
      cardTitle: {
        en: "Agentic Storefronts",
        es: "Agentic Storefronts",
      },
      ownership: {
        en: "Owns the evergreen operational-readiness intent for AI-mediated product discovery and purchasing.",
        es: "Esta página cubre readiness operativo evergreen para descubrimiento y compra mediada por IA.",
      },
      audience: {
        en: "Ecommerce operators responsible for product data, eligibility, measurement, merchandising, and channel governance.",
        es: "Operadores responsables de datos, elegibilidad, medición, merchandising y gobierno de canales.",
      },
      focus: {
        en: "What is announced versus available, product-data quality, merchant controls, measurement, operational ownership, and readiness.",
        es: "Qué está anunciado vs disponible, calidad de datos, controles, medición, ownership y readiness.",
      },
      sources: [SHOPIFY_DEV, SHOPIFY_PRODUCTS],
      paths: {
        en: [
          { label: "Read the dated May 2026 platform update", href: "/resources/shopify-agentic-storefronts-may-2026" },
          { label: "Study the Shopify development architecture", href: "/resources/shopify-development-guide" },
        ],
        es: [
          { label: "Leer la actualización de plataforma de mayo de 2026", href: "/es/recursos/shopify-nuevas-reglas-compradores-ia-mayo-2026" },
          { label: "Estudiar la arquitectura de desarrollo Shopify", href: "/es/recursos/shopify-development-guide" },
        ],
      },
    },
  },
  {
    match: /q1 2026|100b gmv/i,
    strategy: {
      key: "q1-news",
      ownership: {
        en: "Owns dated Q1 2026 Shopify results analysis and operator implications.",
        es: "Esta página cubre análisis fechado de resultados Shopify Q1 2026 e implicaciones para operadores.",
      },
      audience: {
        en: "Ecommerce leaders using platform performance as one input, not the only input, in roadmap and replatform decisions.",
        es: "Líderes ecommerce que usan desempeño de plataforma como un input, no como única razón de decisión.",
      },
      focus: {
        en: "First-party financial figures, what they do and do not imply for merchants, and which developments deserve monitoring.",
        es: "Cifras oficiales, qué implican y qué no para merchants, y qué desarrollos conviene monitorear.",
      },
      status: {
        en: "Dated Q1 2026 analysis. Use Shopify Investor Relations for the latest quarter.",
        es: "Análisis fechado Q1 2026. Usa Investor Relations para el trimestre más reciente.",
      },
      sources: [SHOPIFY_INVESTORS],
    },
  },
  {
    match: /ai vs pro web developers|ai.*web developers/i,
    strategy: {
      key: "ai-build-risk",
      displayTitle: {
        en: "AI Builders vs Professional Shopify Development: Where Each Is Safe in 2026",
        es: "AI builders vs desarrollo Shopify profesional: dónde es seguro usar cada uno en 2026",
      },
      cardTitle: {
        en: "AI Builders vs Pro Development",
        es: "AI builders vs desarrollo pro",
      },
      ownership: {
        en: "Owns risk-based selection between AI building tools and professional Shopify engineering.",
        es: "Esta página cubre selección basada en riesgo entre AI builders e ingeniería Shopify profesional.",
      },
      audience: {
        en: "Ecommerce owners deciding what can be prototyped quickly and what requires accountable production engineering.",
        es: "Owners ecommerce que deciden qué puede prototiparse y qué requiere ingeniería productiva responsable.",
      },
      focus: {
        en: "Data sensitivity, checkout, maintainability, observability, integrations, release risk, and total ownership, not a culture-war comparison.",
        es: "Datos, checkout, mantenibilidad, observabilidad, integraciones, riesgo de release y ownership total.",
      },
      sources: [SHOPIFY_DEV, SHOPIFY_CHECKOUT],
      paths: {
        en: [
          { label: "Study the Shopify development architecture", href: "/resources/shopify-development-guide" },
          { label: "Decide between custom development and apps", href: "/resources/custom-shopify-solutions-for-established-brands" },
          { label: "Evaluate Shopify development services", href: "/shopify-experts" },
        ],
        es: [
          { label: "Estudiar la arquitectura de desarrollo Shopify", href: "/es/recursos/shopify-development-guide" },
          { label: "Decidir entre desarrollo custom y apps", href: "/es/recursos/custom-shopify-solutions-for-established-brands" },
          { label: "Evaluar servicios de desarrollo Shopify", href: "/es/desarrollo-shopify" },
        ],
      },
      linkable: true,
    },
  },
  {
    match: /shopify collabs/i,
    strategy: {
      key: "collabs-operations",
      displayTitle: {
        en: "Shopify Collabs for Established DTC Brands: Economics, Attribution, and Operations",
        es: "Shopify Collabs para marcas DTC establecidas: economía, atribución y operación",
      },
      cardTitle: {
        en: "Shopify Collabs Operations",
        es: "Operación de Shopify Collabs",
      },
      ownership: {
        en: "Owns operational design of a Shopify Collabs program for an established brand.",
        es: "Esta página cubre diseño operativo de Shopify Collabs para una marca establecida.",
      },
      audience: {
        en: "Growth and ecommerce teams that need commission economics, attribution discipline, creator quality, fraud controls, and repeatable operations.",
        es: "Equipos growth y ecommerce que necesitan economía de comisiones, atribución, calidad, control de fraude y operación repetible.",
      },
      focus: {
        en: "Program economics, attribution, creator selection, disclosures, operational ownership, and measurement, not app-install instructions alone.",
        es: "Economía, atribución, selección, disclosures, ownership y medición, no sólo instalar la app.",
      },
      sources: [SHOPIFY_COLLABS, FTC_ENDORSEMENTS],
    },
  },
  {
    match: /become a shopify developer|convertirte.*developer|ser.*developer.*shopify/i,
    strategy: {
      key: "developer-career",
      displayTitle: {
        en: "How to Become a Shopify Developer in 2026: Skills, Portfolio Projects, and a Real Learning Path",
        es: "Cómo convertirte en Shopify Developer en 2026: skills, proyectos de portafolio y ruta real de aprendizaje",
      },
      metaTitle: {
        en: "How to Become a Shopify Developer (2026 Roadmap)",
        es: "Cómo convertirte en desarrollador Shopify (ruta 2026)",
      },
      cardTitle: {
        en: "Become a Shopify Developer",
        es: "Conviértete en Shopify Developer",
      },
      ownership: {
        en: "Owns the learning-roadmap and portfolio-building intent for aspiring Shopify developers.",
        es: "Esta página cubre la ruta de aprendizaje y construcción de portafolio para futuros Shopify developers.",
      },
      audience: {
        en: "Aspiring developers who want a credible path from Liquid fundamentals to production Shopify work.",
        es: "Developers en formación que quieren pasar de fundamentos Liquid a trabajo Shopify productivo.",
      },
      focus: {
        en: "What to learn, in what order, projects that prove skill, how to read official docs, how to contribute useful examples, and how to earn trust.",
        es: "Qué aprender, en qué orden, proyectos que prueban habilidad, cómo usar documentación, contribuir ejemplos y ganar confianza.",
      },
      status: {
        en: "Learning paths change as Shopify APIs and tools evolve. Treat the official developer documentation as the source of truth.",
        es: "La ruta cambia con APIs y herramientas. La documentación oficial es la fuente de verdad.",
      },
      sources: [SHOPIFY_DEV, SHOPIFY_FUNCTIONS, SHOPIFY_CHECKOUT, SHOPIFY_PRODUCTS],
      paths: {
        en: [
          { label: "Study the Shopify development architecture", href: "/resources/shopify-development-guide" },
          { label: "Build a Liquid recommender project", href: "/resources/shopify-liquid-product-recommender" },
          { label: "Understand Shopify Functions", href: "/resources/shopify-functions" },
        ],
        es: [
          { label: "Estudiar la arquitectura de desarrollo Shopify", href: "/es/recursos/shopify-development-guide" },
          { label: "Construir un recommender en Liquid", href: "/es/recursos/shopify-liquid-product-recommender" },
          { label: "Entender Shopify Functions", href: "/es/recursos/shopify-functions" },
        ],
      },
      linkable: true,
    },
  },
];

function haystack(resource: ResourceLike) {
  return [
    resource.slug,
    resource.slug_es,
    resource.title_en,
    resource.title_es,
    ...(resource.tags_en ?? []),
    ...(resource.tags_es ?? []),
  ]
    .filter(Boolean)
    .join(" ");
}

function fallbackStrategy(resource: ResourceLike): ResourceEditorialStrategy {
  const cluster = resource.cluster ?? "general";
  const common = {
    audience: {
      en: "Established Shopify and Shopify Plus operators who need a clear decision, not generic ecommerce advice.",
      es: "Operadores Shopify y Shopify Plus establecidos que necesitan una decisión clara, no consejos genéricos.",
    },
    focus: {
      en: "The business symptom, evidence to collect, Shopify-specific constraints, available options, trade-offs, and the responsible next step.",
      es: "El síntoma de negocio, la evidencia, restricciones Shopify, opciones, trade-offs y siguiente paso responsable.",
    },
  };

  if (cluster === "performance") {
    return { key: "performance-general", ownership: { en: "Owns a specific Shopify performance problem without replacing the performance pillar.", es: "Cubre un problema específico de performance sin sustituir el pilar." }, ...common, sources: [WEB_VITALS, WEB_VITALS_TOOLS, SHOPIFY_THEME_PERFORMANCE] };
  }
  if (cluster === "technical-seo") {
    return { key: "seo-general", ownership: { en: "Owns a bounded Shopify technical SEO implementation problem.", es: "Cubre un problema acotado de implementación SEO técnico." }, ...common, sources: [GOOGLE_CANONICAL, GOOGLE_STRUCTURED_DATA] };
  }
  if (cluster === "migration") {
    return { key: "migration-general", ownership: { en: "Owns one bounded migration decision or workstream.", es: "Cubre una decisión o workstream acotado de migración." }, ...common, sources: [SHOPIFY_MIGRATION, SHOPIFY_REDIRECTS, GOOGLE_SITE_MOVE] };
  }
  if (cluster === "conversion") {
    return { key: "conversion-general", ownership: { en: "Owns one bounded conversion problem without replacing the CRO operating model.", es: "Cubre un problema de conversión sin sustituir el modelo operativo de CRO." }, ...common, sources: [SHOPIFY_CHECKOUT, WEB_VITALS] };
  }
  return { key: "general", ownership: { en: "Owns a bounded Shopify operator decision.", es: "Cubre una decisión acotada para operadores Shopify." }, ...common, sources: [SHOPIFY_DEV] };
}

export function getResourceEditorialStrategy(resource: ResourceLike): ResourceEditorialStrategy {
  const text = haystack(resource);
  return RULES.find(({ match }) => match.test(text))?.strategy ?? fallbackStrategy(resource);
}

export function editorialText(value: { en: string; es: string } | undefined, locale: Locale) {
  return value?.[locale];
}

export function normalizeEditorialBody(body: string, resource: ResourceLike, locale: Locale) {
  if (!body) return body;
  const strategy = getResourceEditorialStrategy(resource);
  let next = body
    .replaceAll("/resources/shopify-plus-migration-masterclass", "/resources/shopify-replatforming-playbook")
    .replaceAll("/es/recursos/masterclass-migracion-shopify-plus", "/es/recursos/playbook-replatforming-shopify");

  const repeatedOpenings = locale === "es"
    ? [
        /La mayor[ií]a de (?:las )?tiendas Shopify se equivoca(?:n)? en esto[^\n]*\n*/i,
        /La mayor[ií]a de los desarrollos Shopify rompen el revenue en lugares que nadie revisa\.?\n*/i,
      ]
    : [
        /Most Shopify stores get this wrong[^\n]*\n*/i,
        /Most Shopify builds break revenue in places nobody checks\.?\n*/i,
      ];
  for (const pattern of repeatedOpenings) next = next.replace(pattern, "");

  if (strategy.key === "shopify-replatforming-playbook") {
    next = next
      .replace(/Why brands replatform to Shopify Plus/gi, "Why brands replatform to Shopify")
      .replace(/Por qu[eé] las marcas migran a Shopify Plus/gi, "Por qué las marcas migran a Shopify")
      .replace(/The brands we take to Plus usually share/gi, "The brands we replatform to Shopify or Shopify Plus usually share")
      .replace(/Las marcas que llevamos a Plus suelen compartir/gi, "Las marcas que migramos a Shopify o Shopify Plus suelen compartir")
      .replace(/Magento\s*\/\s*Adobe Commerce\s*→\s*Plus/gi, "Magento / Adobe Commerce → Shopify or Shopify Plus")
      .replace(/BigCommerce\s*→\s*Plus/gi, "BigCommerce → Shopify or Shopify Plus")
      .replace(/WooCommerce\s*→\s*Plus/gi, "WooCommerce → Shopify or Shopify Plus")
      .replace(/Custom stack\s*→\s*Plus or Hydrogen/gi, "Custom stack → Shopify or Hydrogen")
      .replace(/Stack custom\s*→\s*Plus o Hydrogen/gi, "Stack custom → Shopify o Hydrogen")
      .replace(/What a Shopify Plus migration actually costs/gi, "What enterprise Shopify replatforming actually costs")
      .replace(/Lo que realmente cuesta una migraci[oó]n a Shopify Plus/gi, "Lo que realmente cuesta un replatform enterprise a Shopify")
      .replace(/Migrate to Shopify Plus with a senior team/gi, "Migrate to Shopify with a senior team")
      .replace(/Migra a Shopify Plus con un equipo senior/gi, "Migra a Shopify con un equipo senior");
  }

  if (strategy.key === "checkout-extensibility") {
    next = next
      .replace(/in seven weeks/gi, "under the platform's published transition schedule")
      .replace(/en siete semanas/gi, "según el calendario de transición publicado por Shopify");
  }

  if (strategy.key === "scripts-recovery") {
    next = next
      .replace(/before June 30/gi, "after the June 30, 2026 deprecation")
      .replace(/before April/gi, "when validating or rebuilding inherited logic")
      .replace(/antes del 30 de junio/gi, "después de la deprecación del 30 de junio de 2026")
      .replace(/antes de abril/gi, "al validar o reconstruir lógica heredada");
  }

  if (strategy.key === "diagnostic-index") {
    next = next
      .replace(/they're probably costing you 10[–-]30% of revenue\.?/gi, "the right next step is to collect evidence before estimating the commercial impact.")
      .replace(/probablemente te est[aá] costando 10[–-]30% del revenue\.?/gi, "el siguiente paso responsable es recopilar evidencia antes de estimar el impacto comercial.");
  }

  const priorityEnhanced = applyPriorityContentEnhancement(
    next,
    strategy.key,
    locale,
    resource.slug,
    resource.slug_es,
  );
  return applyMigrationContentEnhancement(priorityEnhanced, strategy.key, locale);
}
